(ns hai.library.sources.table
  "Sources table"
  (:require
   [clojure.string :as str]
   [com.wsscode.pathom3.connect.operation :as pco]
   [hai.library.sources.documents-table :as documents-table]
   [hai.library.sources.utils :as sources-utils]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.select.single-select :as single-select]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.ui.utils.polling :as polling]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]))

;; Modal state management
(defevent ::open-modal
  [db source-uuid]
  (assoc db ::modal-open? true
            ::selected-source-uuid source-uuid))

(defevent ::close-modal [db]
  (assoc db ::modal-open? false
            ::selected-source-uuid nil))

(defsub ::modal-open? [db]
  (get db ::modal-open? false))

(defsub ::selected-source-uuid [db]
  (get db ::selected-source-uuid))

(defsub ::table-sources
  [sources <- [:table/rows ::table]]
  sources)

(defsub ::selected-source
  [sources <- ::table-sources
   selected-uuid <- ::selected-source-uuid]
  (when selected-uuid
    (->> sources
         (filter #(= (:ai-source/uuid %) selected-uuid))
         first)))

(defn documents-modal-container
  "Container that subscribes to modal state and renders the documents modal"
  []
  (let [selected-source @(<s ::selected-source)]
    (when selected-source
      [documents-table/documents-modal
       selected-source
       true
       #(>e [::close-modal])])))

(defn title-render
  [source assistant-uuid]
  [table/table-cell
   [c/link-button
    {:on-click (fn [_]
                 (if assistant-uuid
                   (>e [:routing/navigate [:ai-sources/show source {:ai-model-config/uuid assistant-uuid}]])
                   (>e [:routing/navigate [:ai-sources/show source]])))}
    (:ai-source/title source)]])

(defn type-render
  [source]
  [table/table-cell
   (:ai-source/type-label source)])

(defn status-render [source]
  [table/table-cell {:data-uuid (:ai-source/uuid source)}
   (let [status (:ai-source/status source)
         color (case status
                 :done "green"
                 :processing "orange"
                 :error "red"
                 "gray")]
     [c/Badge {:color color :radius "full" :variant "solid" :p "2"}
      (if status
        (-> status name str/capitalize)
        "Unknown")])])

(o/defstyled view-docs-link :span
  :text-blue-600 :cursor-pointer :ml-2 :text-sm
  {:&:hover {:text-decoration "underline"}})

(defn documents-render
  [source]
  [table/table-cell
   [:div
    [:span (str (count (:ai-source/documents source)))]
    [view-docs-link
     {:on-click #(>e [::open-modal (:ai-source/uuid source)])}
     "(view documents)"]]])

(def retry-icon (icons/mui-icon :filled :replay))

(o/defstyled retry-button :button
  :cursor-pointer :p-1 :rounded :flex :items-center :justify-center
  :border-none :bg-transparent
  {:transition "all 0.2s ease"}
  [:&:hover
   {:background-color "rgba(0, 0, 0, 0.05)"}]
  [:&:active
   {:transform "scale(0.95)"}])

(defn retry-render
  [source]
  [table/table-cell
   (when-not (:ai-source/in-progress? source)
     [retry-button
      {:on-click #(>e [::retry-source (:ai-source/uuid source)])
       :title "Retry ingestion"}
      [retry-icon {:style {:width "20px" :height "20px"}}]])])

(defevent-fx ::retry-source
  [{:keys [_db]} source-uuid]
  {:fetch {:url (str "/assistant/v0/sources/" source-uuid "/retry")
           :method :post
           :accept :json
           :on-success-fx [[:dispatch [:toast/success {:text "Source retry started successfully"}]]
                           [:dispatch [::polling/start {:id :sources-table-poll
                                                        :table :hai.library.sources.table/table
                                                        :interval-ms 10000
                                                        :stop-condition-fn sources-utils/all-sources-finished?}]]]}})

(defn table
  [config]
  (let [ident [:ai-model-config/uuid (:ai-model-config/uuid config)]
        assistant-uuid (:ai-model-config/uuid config)]
    [collection/component
     (merge {:id (or (:id config)
                     ::table)
             :paginate? true
             :no-items (fn [_]
                         [c/Flex {:p "4"}
                          [c/Text [c/Strong "Nothing here yet."] " Upload knowledge above to get started."]])
             :query [{ident
                      [{:user.sources/table
                        [{:collection/items
                          [:ai-source/title
                           :ai-source/uuid
                           :ai-source/type
                           :ai-source/type-label
                           :ai-source/status
                           :ai-source/in-progress?
                           :user.entity/permissions
                           (pco/? :ai-source/documents)]}]}]}]
             ;; TODO default card render
             :card (fn [collection item]
                     [card/card
                      [cards/card-select-checkbox collection item]
                      [cards/column-render collection item]])
             :columns [{:header "Title"
                        :render (fn [source] (title-render source assistant-uuid))
                        :attr   :ai-source/title
                        :sort?  true}
                       {:header "Type"
                        :render type-render
                        :attr   :ai-source/type
                        :sort?  true
                        :filter {:schema      :keyword
                                 :type        :filter.remote/select
                                 :placeholder "Type..."
                                 :field       single-select/field}}
                       {:header "Status"
                        :render status-render
                        :attr   :ai-source/status
                        :sort?  true
                        :filter {:schema      :keyword
                                 :type        :filter.remote/select
                                 :placeholder "Status..."
                                 :field       single-select/field}}
                       {:header "Documents"
                        :render documents-render
                        :type   :ai-source/documents
                        :sort?  false}
                       {:header "Retry"
                        :render retry-render
                        :sort?  false}]
             :bulk-actions [{:title     "Delete"
                             :mutation  'source/delete
                             :disabled? bulk-actions/disabled?
                             :alert     {:content (fn [{:table/keys [selected] :as table} action]
                                                    [bulk-actions/confirm-dialog
                                                     {:table  table
                                                      :action action}
                                                     [bulk-actions/title "Confirm Deletion of Sources"]
                                                     [bulk-actions/description "Are you sure you want to delete the following Sources?"]
                                                     (into [:ul]
                                                           (map (fn [{:ai-source/keys [title]}]
                                                                  [:li [:strong title]]))
                                                           selected)])}}]}
            (when (:on-load-success config)
              {:on-load-success (:on-load-success config)}))]))

(o/defstyled polling-indicator :div
  :flex :items-center :gap-2 :text-sm :text-gray-600 :mb-4
  ["@keyframes spin"
   ["from" {:transform "rotate(0deg)"}]
   ["to" {:transform "rotate(360deg)"}]]
  [:.spin-icon
   {:animation "spin 1s linear infinite"}])

(def sync-icon (icons/mui-icon :outlined :sync))

(defn table-wrapper
  [config]
  (let [modal-open? @(<s [::modal-open?])
        polling-active? @(<s [::polling/active? :sources-table-poll])
        interval-ms @(<s [::polling/interval-ms :sources-table-poll])]
    [:<>
     (when polling-active?
       [polling-indicator
        [:div.spin-icon
         [sync-icon]]
        [:span (str "Auto-refreshing every " (/ interval-ms 1000) " seconds...")]])
     [table config]
     (when modal-open?
       [documents-modal-container])]))

(defn page
  [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/lead
    "Your knowledge base. Upload files, add documents and connect data — train your assistant once, use it everywhere."]
   [layout/content
    [table-wrapper]]])
